"use client";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

import cn from "classnames";
import { PropsWithChildren, ReactElement, useState } from "react";
import { Navigation, Autoplay } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import CardCarouselButtons from "./CardCarouselButtons";
import styles from "./CardCarousel.module.scss";

type SwiperBreakpoint = {
  slidesPerView: number;
  spaceBetween?: number;
};

interface CardCarouselProps {
  classes?: {
    container?: string;
    swiper?: string;
    buttonsContainer?: string;
    buttonClasses?: {
      prev?: string;
      next?: string;
    };
    swiperEmptySlot?: string;
  };
  slidesPerView?: number;
  spaceBetween?: number;
  breakpoints?: Record<number, SwiperBreakpoint>;
  loop?: boolean;
  centerSlides?: boolean;
  id: string;
  initialSlide?: number;
  showButtons?: boolean;
  pagination?: {
    mobile?: boolean;
    color?: "primary" | "secondary";
    iconType?: "lines" | "dots";
  };
  handleSwiper?: (swiper: any) => void;
  handleSlideChange?: (swiper: any) => void;
  autoplay?: {
    delay?: number;
    disableOnInteraction?: boolean;
  };
}

function CardCarousel({
  classes,
  breakpoints,
  slidesPerView,
  spaceBetween = 12,
  children,
  centerSlides,
  loop,
  id,
  initialSlide,
  pagination,
  showButtons = true,
  handleSwiper,
  handleSlideChange,
  autoplay,
}: PropsWithChildren<CardCarouselProps>) {
  const [beginOrEnd, setBeginOrEnd] = useState<"begin" | "end" | undefined>(undefined);
  const paginationEl = `ccl-${id}`;

  /**
   * Swiper uses class name to add required onClick for prev/next functionality
   * Set the max number of results (todo: move this to CMS global web config)
   */
  const [prevButton] = useState<string>(`ccl-prev_${id}`);
  const [nextButton] = useState<string>(`ccl-next_${id}`);

  /**
   * this creates the pagination items for the bottom of the screen on mobile
   * Swiper does not accept the react/JSX way of rendering components and must return a string
   */
  const renderPaginationItem = (index: number, className: string) => {
    const itemStyles = cn(
      styles.paginationItem,
      pagination?.color === "primary" ? styles.primary : styles.secondary,
      pagination?.iconType === "dots" ? styles.dots : undefined
    );
    return `<span class="${className} ${itemStyles}"></span>`;
  };

  if (!children || !Array.isArray(children)) {
    return <></>;
  }

  return (
    <>
      {showButtons && (
        <CardCarouselButtons
          prevClassName={prevButton}
          nextClassName={nextButton}
          beginOrEnd={beginOrEnd}
          className={classes?.buttonsContainer}
          buttonClasses={classes?.buttonClasses}
        />
      )}
      <div className={classes?.container}>
        <Swiper
          className={classes?.swiper}
          modules={[Navigation, Autoplay]}
          onSwiper={(swiper) => {
            if (typeof handleSwiper === "function") {
              handleSwiper(swiper);
            }
          }}
          onSlideChange={(swiper: any) => {
            if (beginOrEnd) {
              setBeginOrEnd(undefined);
            }

            if (typeof handleSlideChange === "function") {
              handleSlideChange(swiper);
            }
          }}
          initialSlide={initialSlide}
          centeredSlides={centerSlides}
          centerInsufficientSlides={centerSlides}
          centeredSlidesBounds={centerSlides}
          slidesPerView={slidesPerView}
          loop={loop}
          spaceBetween={spaceBetween ?? 12}
          breakpoints={breakpoints}
          onReachBeginning={() => setBeginOrEnd("begin")}
          onReachEnd={() => setBeginOrEnd("end")}
          navigation={{
            prevEl: `#${prevButton}`,
            nextEl: `#${nextButton}`,
          }}
          pagination={
            pagination?.mobile
              ? {
                  el: `.${paginationEl}`,
                  clickable: true,
                  renderBullet: renderPaginationItem,
                }
              : false
          }
          autoplay={autoplay}
        >
          {(children ?? []).map((card: ReactElement, i: number) => (
            <SwiperSlide key={i}>{card}</SwiperSlide>
          ))}
          {classes?.swiperEmptySlot && (
            <SwiperSlide>
              <div className={classes.swiperEmptySlot} />
            </SwiperSlide>
          )}
        </Swiper>
      </div>
    </>
  );
}

export default CardCarousel;
